import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DomSanitizer } from '@angular/platform-browser';
import * as QRCode from 'qrcode';
import html2canvas from 'html2canvas';
@Injectable({
  providedIn: 'root'
})
export class SharedService {

  private sidenavToggleSubject = new Subject<void>();
  sidenavToggle$ = this.sidenavToggleSubject.asObservable();

  private pageTypeSubject = new BehaviorSubject<{ data: any; typePage: string } | null>(null);
  pageType$ = this.pageTypeSubject.asObservable();

  sendPageType(info: { data: any; typePage: string }) {
    this.pageTypeSubject.next(info);
  }

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
  ) { }

  toggle() {
    this.sidenavToggleSubject.next();
  }

  setRequiredField(form: FormGroup, field: string) {
    form.addControl(field, new FormControl());
    if (field == 'PWD') {
      form.controls[field]?.setValidators([Validators.required]);
    }
    form.controls[field]?.updateValueAndValidity();
  }

  getTimestamp(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${yyyy}${mm}${dd}_${hh}${min}${ss}`;
  }

  exportExcel(
    dataSource: any,
    dataColumn: string[],
    title: any,
    filename: string,
    headerLabels?: string[],
    dynamicColumns: { [key: string]: (row: any) => any } = {},
    tableTotals?: any
  ) {

    const exportColumns = dataColumn.filter(col => col.toLowerCase() !== 'actions');

    const exportHeaders = headerLabels
      ? headerLabels.filter((_, i) => dataColumn[i].toLowerCase() !== 'actions')
      : undefined;


    const exportData = dataSource.map((row: any) => {
      const obj: any = {};

      exportColumns.forEach((col, index) => {
        const key = exportHeaders ? exportHeaders[index] : col;
        obj[key] = dynamicColumns[col] ? dynamicColumns[col](row) : row[col] ?? row.record?.[col] ?? '';
      });
      return obj;
    });

    // Append totals row if provided
    if (tableTotals && title === 'Registry Statistics') {
      const totalRow: any = {};
      exportColumns.forEach((col, index) => {
        const key = headerLabels ? headerLabels[index] : col;
        if (index === 0) {
          // Include number of rows in the first column
          totalRow[key] = `Total (${tableTotals.rows || 0} Records)`;
        } else {
          totalRow[key] = tableTotals[col] ?? '';
        }
      });
      exportData.push(totalRow);
    }

    
     const worksheet = XLSX.utils.aoa_to_sheet([]);

  // 2️⃣ Add report title (Row 1)
  const reportTitle = `${title} Report`;
  XLSX.utils.sheet_add_aoa(worksheet, [[reportTitle]], { origin: 'A1' });

  // 3️⃣ Add table (headers + data) starting at Row 2
  XLSX.utils.sheet_add_json(worksheet, exportData, {
    origin: 'A2',
    skipHeader: false
  });

  // Merge title across columns
  if (!worksheet['!merges']) worksheet['!merges'] = [];
  worksheet['!merges'].push({
    s: { r: 0, c: 0 },
    e: { r: 0, c: exportColumns.length - 1 }
  });

    // Optionally, style the totals row bold
    if (tableTotals && title === 'Registry Statistics') {
      const totalsRowIndex = exportData.length + 1; // +1 because AOA title takes row 1
      for (let colIndex = 0; colIndex < exportColumns.length; colIndex++) {
        const cellAddress = XLSX.utils.encode_cell({ r: totalsRowIndex, c: colIndex });
        const cell = worksheet[cellAddress];
        if (cell) {
          cell.s = cell.s || {};
          cell.s.font = { bold: true };
        }
      }
    }

    const workbook = XLSX.utils.book_new();

    const timestamp = this.getTimestamp();
    const name = `${filename}_${timestamp}`;
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, name + '.xlsx');
  }

  exportPDF(
    dataSource: any,
    dataColumn: string[],
    title: any,
    filename: string,
    headerLabels?: string[],
    dynamicColumns: { [key: string]: (row: any) => any } = {},
    tableTotals?: any
  ) {
    const doc = new jsPDF();

    const exportColumns = dataColumn.filter(col => col.toLowerCase() !== 'actions');

    const exportHeaders = headerLabels
      ? headerLabels.filter((_, i) => dataColumn[i].toLowerCase() !== 'actions')
      : undefined;
    // --- FIX: Add Header Title ---
    const reportTitle = `${title} Report`;
    const docWidth = doc.internal.pageSize.getWidth(); // Get page width for centering
    const centerPosition = docWidth / 2;

    // Set font style/size for the title
    doc.setFontSize(16);
    doc.setFont('bold'); // Make the title bold

    // Add the title text, centered at the top of the page (e.g., 15 units down)
    doc.text(reportTitle, centerPosition, 10, { align: 'center' });

    // Reset font back to normal before the table
    doc.setFontSize(10);
    doc.setFont('normal');
    // --- END FIX ---

    // Prepare table body
    const bodyData = dataSource.map((row: any) =>
      exportColumns.map(col =>
        dynamicColumns[col]
          ? dynamicColumns[col](row)
          : row[col] ?? row.record?.[col] ?? ''
      )
    );

    // Append totals row if provided
    if (tableTotals && title === 'Registry Statistics') {
      const totalRow = exportColumns.map((col, index) => {
        if (index === 0) return `Total (${tableTotals.rows || 0} Records)`; // first column
        return tableTotals[col] ?? '';
      });
      bodyData.push(totalRow);
    }


    autoTable(doc, {
      head: [exportHeaders ?? exportColumns], // friendly headers here
      body: bodyData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      didParseCell: (data) => {
        // Make totals row bold
        if (tableTotals && data.row.index === bodyData.length - 1 && title == 'Registry Statistics') {
          data.cell.styles.fontStyle = 'bold';
        }
      }

    });
    const timestamp = this.getTimestamp();
    const name = `${filename}_${timestamp}`
    doc.save(name + '.pdf');
  }

  getPageFromLink = (link: any): number | null => {
    if (!link) return null;
    if (typeof link === 'number') return Number(link);
    if (typeof link === 'string') {
      // try URL parsing
      try {
        const url = new URL(link);
        const p = url.searchParams.get('page');
        if (p) return parseInt(p, 10);
      } catch (e) {
        // fallback to regex if not a full URL
        const m = link.match(/page=(\d+)/);
        if (m && m[1]) return parseInt(m[1], 10);
      }
    }
    return null;
  };

  getErrorMessage(field: string, form: FormGroup, fieldLabel?: string): string | null {
    if (!form) return null;
    const control = form?.get(field);

    if (control && control.errors && (control.dirty || control.touched)) {
      // If no label provided, fall back to auto-capitalize field name
      const label = fieldLabel ?? field.charAt(0).toUpperCase() + field.slice(1);
      if (control.errors['pattern']) {
        return `Invalid ${label.toLowerCase()} format`;
      }
      if (control.errors['required']) {
        return `${label} is required`;
      }
      if (control.errors['email']) {
        return `Enter a valid ${label}`;
      }
      if (control.errors['minlength']) {
        return `${label} must be at least ${control.errors['minlength'].requiredLength} characters`;
      }
      if (control.errors['maxlength']) {
        return `${label} cannot exceed ${control.errors['maxlength'].requiredLength} characters`;
      }
      if (control.errors['invalidDate']) {
        return control.errors['invalidDate'];
      }
      if (control.errors['invalidPhilippineMobile']) {
        return 'Please enter a valid Philippine mobile number';
      }
    }

    return null;
  }

  async spIDTemplateData(details) {
    const card: any = details.details;
    const now = new Date(card?.qrcode?.dateIssued);
    const plusOneYear = new Date(card?.qrcode?.dateIssued);
    plusOneYear.setFullYear(now.getFullYear() + 1);

    const validUntil = this.formatDateMMDDYYYY(plusOneYear);
    const spDemographics = card?.demographic?.filter(
      (demo) => demo.code === 'SP' && demo.status === 'verified'
    ) || [];

    // ✅ Join their typeValue (e.g. "a7 / c / a5")
    const joinedTypeValues = spDemographics
      .map(d => d.typeValue)
      .filter(Boolean)
      .join(', ');

    // ✅ Optionally join their type descriptions too if needed
    const joinedTypeDescriptions = spDemographics
      .map(d => d.type)
      .filter(Boolean)
      .join(' / ');

    const family_members = card?.family_members?.filter(
      (member) => member.beneficiary === 'yes'
    ) || [];

    let card_data: any = {
      template: 'SP',
      uuid: card?.uuid,
      id: card?.unified_id,
      picture: null,
      signature: null,
      fullName: `${card?.lguLast || ''}, ${card?.lguFirst || ''} ${card?.lguMiddle || ''} ${card?.lguExt || ''}`,
      gender: (card?.lguGender === 'M') ? 'MALE' : 'FEMALE',
      dob: card?.lguBDM + '/' + card?.lguBDD + '/' + card?.lguBDY,
      address: `${card?.lguBarangay}, ${card?.lguMunicity}, ${card?.lguProvince}`,
      validUntil,
      emergency_contact: (card?.lguContact) ? card?.lguContact : '',
      emergency_contact_no: (card?.lguContactNo) ? '(+63) ' + card?.lguContactNo.substring(1) : '',
      emergency_contact_address: '',
      qrcode: card?.qrcode,
      sp_category: joinedTypeValues,
      qualification_code: '',
      dependents: family_members,
      city: card?.lguCity,
      province: card?.lguProvince,
      mayor: card?.scp_org?.lguMayorName,
      cimswdo: card?.createdBy,
      civil_status: card?.lguCivil,
    }

    // fullname
    const name = [`${card?.lguLast},`, card?.lguFirst, card?.lguExt];
    if (card?.lguMiddle) {
      name.push(`${card?.lguMiddle.charAt(0)}.`);
    }

    // full address
    if (card?.lguStreet)
      card_data.address = `${card?.lguStreet}, ${card_data.address}`;

    // demographic benefits    
    card_data.qualification_code = card?.demographic_benefits
      ?.filter(b => b?.status === 'active' && b?.benefit?.status === 'active')
      ?.map(b => b?.benefit?.benefitCode)
      ?.join(', ') || 'N/A';

    /** generate qrcode */
    const svg = await QRCode.toString(JSON.stringify(card.qrcode), {
      type: 'svg', margin: 0, width: 180, errorCorrectionLevel: 'H'
    });

    const svgWithLogo = svg.replace(
      '</svg>',
      `<rect x="31.5" y="31.5" width="26" height="26" fill="white" />` +
      `<image x="34.5" y="35.5"
                width="20" height="20"
                xlink:href="/assets/img/admin/id-template/dswd-logo.png" /></svg>`
    );

    card_data.qrcode = this.sanitizer.bypassSecurityTrustHtml(svgWithLogo);
    /** end generate qrcode */

    if (card.documents.length > 0) {
      card.documents.forEach(doc => {
        switch (doc.type.docName) {
          case 'Profile Picture':
            card_data.picture = doc.docUrl;
            break;
          case 'eSignature':
            card_data.signature = doc.docUrl;
            break;
          default:
          // do nothing
        }
      });
    }

    return card_data;
  }

  async pwdIDTemplateData(details) {
    const card: any = details.details;
    const now = new Date(card?.qrcode?.dateIssued);
    const plusFiveYears = new Date(card?.qrcode?.dateIssued);
    plusFiveYears.setFullYear(now.getFullYear() + 5);

    const dateIssued = this.formatDateMMDDYYYY(now);
    const validUntil = this.formatDateMMDDYYYY(plusFiveYears);

    let card_data: any = {
      uuid: card?.uuid,
      template: 'PWD',
      id: card?.unified_id,
      picture: null,
      signature: null,
      fullname: '',
      gender: (card?.lguGender === 'M') ? 'MALE' : 'FEMALE',
      dob: card?.lguBDM + '/' + card?.lguBDD + '/' + card?.lguBDY,
      civil_status: card?.lguCivil,
      blood_type: card?.bloodType,
      address: `${card?.lguBarangay}, ${card?.lguMunicity}, ${card?.lguProvince}`,
      disabilities: card.demographic.filter(item => item.code !== 'IP').map(item => item.type).join(', '),
      dateIssued,
      validUntil,
      emergency_contact: (card?.lguContact) ? card?.lguContact : '',
      emergency_contact_no: (card?.lguContactNo) ? '(+63) ' + card?.lguContactNo.substring(1) : '',
      qrcode: ''
    }

    const name = [`${card?.lguLast},`, card?.lguFirst, card?.lguExt];
    if (card?.lguMiddle) {
      name.push(`${card?.lguMiddle.charAt(0)}.`);
    }
    card_data.fullname = name.join(' ');

    if (card?.lguStreet)
      card_data.address = `${card?.lguStreet}, ${card_data.address}`;

    /** generate qrcode */
    const svg = await QRCode.toString(JSON.stringify(card.qrcode), {
      type: 'svg', margin: 0, width: 164, errorCorrectionLevel: 'H'
    });
    card_data.qrcode = this.sanitizer.bypassSecurityTrustHtml(svg);
    /** end generate qrcode */

    if (card.documents.length > 0) {
      card.documents.forEach(doc => {
        switch (doc.type.docName) {
          case 'Profile Picture':
            card_data.picture = doc.docUrl;
            break;
          case 'eSignature':
            card_data.signature = doc.docUrl;
            break;
          default:
          // do nothing
        }
      });
    }

    return card_data;
  }

  formatDateMMDDYYYY(date: Date): string {
    const mm = (date.getMonth() + 1).toString().padStart(2, '0');  // months are 0-based
    const dd = date.getDate().toString().padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  async urlToBase64ViaProxy(s3Url: string): Promise<string> {
    const proxyUrl =
      `${environment.apiUrl}api/v1/public/image-proxy?url=${encodeURIComponent(s3Url)}`;

    const response = await fetch(proxyUrl);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  isBase64Image(value: string): boolean {
    return value.startsWith('data:image/');
  }

  async htmlCanvas(element: HTMLElement): Promise<string> {
    // Inline SVG <image> tags
    await this.inlineSVGImages(element);

    // Wait for all images to load
    await this.waitForImages(element);

    try {
      await document.fonts.ready;
      const canvas: any = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        proxy: `${environment.apiUrl}api/v1/public/image-proxy`,
      })
      const imgData = canvas.toDataURL('image/png');
      return imgData;

    } catch (e) {
      console.error('Error creating image:', e);
      throw e;
    }
  }
  private waitForImages(element: HTMLElement): Promise<void> {
    const images = Array.from(element.querySelectorAll('img')) as HTMLImageElement[];
    const promises = images.map(img => {
      if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
      return new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => {
          console.warn('Image failed to load:', img.src);
          resolve(); // resolve anyway to avoid blocking
        };
      });
    });
    return Promise.all(promises).then(() => { });
  }

  // Inline SVG images as Base64
  private async inlineSVGImages(element: HTMLElement) {
    const svgImages = element.querySelectorAll('svg image');
    for (const img of Array.from(svgImages)) {
      const hrefAttr = img.getAttribute('href') || img.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
      if (!hrefAttr) continue;

      try {
        const dataUrl = await fetch(hrefAttr)
          .then(res => res.blob())
          .then(blob => new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          }));

        img.setAttribute('href', dataUrl);
      } catch (err) {
        console.warn('Failed to inline SVG image:', hrefAttr, err);
      }
    }
  }
}
